import React, { useState } from 'react'
import { Typography } from '@material-ui/core'
import styled from 'styled-components'
import ShowPageInDrawer from '../../components/ShowPageInDrawer'
import { BorderedCard, ContactDetailsContainer, LinkAvatar, LinkAvatarContainer, StyledWorkspacedLink } from './HomeContent'
import { graphql } from 'babel-plugin-relay/macro'
import useLazyLoadQueryWithSubscription from '../../hooks/useLazyLoadQueryWithSubscription'
import { SharedContactDetailsQuery } from '../../__generated__/SharedContactDetailsQuery.graphql'
import ShareContact from '../../components/ShareContact'
import Button from '../../components/Button'

const NonOverflowText = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
`
const TitleText = styled(NonOverflowText)`
  line-height: 1;
`
const ContactRow = styled.div`
  flex-wrap: wrap;
  gap: 16px;

  > div {
    flex: 1 1 0;
    min-width: 200px;
  }
`
const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 4px;
`
const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`
const DetailsContainer = styled.div`
  margin: 4px 0;
`
interface ContactListProps {
  header: string
  contactRole: string
  list: ReadonlyArray<{ slug: string; name?: string; profile_url?: string }>
  slug: string
}

const ContactList: React.FC<ContactListProps> = ({ header, contactRole, list, slug }) => {
  const [openContactSlug, setOpenContactSlug] = useState<string | null>(null)
  const [shareDriver, setShareDriver] = useState<boolean>(false)
  const [shareTransporter, setShareTransporter] = useState<boolean>(false)
  const driverRole = 'driver'
  const transporterRole = 'transporter'

  const setShareRole = () => {
    if (header === 'Transporters') {
      setShareTransporter(true)
    } else {
      setShareDriver(true)
    }
  }
  return (
    <BorderedCard>
      <ShareContact open={shareDriver} onClose={() => setShareDriver(false)} slug={slug} role={driverRole}></ShareContact>
      <ShareContact open={shareTransporter} onClose={() => setShareTransporter(false)} slug={slug} role={transporterRole}></ShareContact>
      <ShowPageInDrawer
        entityType='contact'
        entitySlug={openContactSlug || ''}
        open={Boolean(openContactSlug)}
        onClose={() => setOpenContactSlug(null)}
      />
      <TitleContainer>
        <Typography variant='h6'>{header}</Typography>
        <Button variant='contained' onClick={() => setShareRole()}>{`Add ${header}`}</Button>
      </TitleContainer>
      <DetailsContainer>
        {list.length > 0 ? (
          <ContactContainer>
            {list.map((item) => (
              <StyledWorkspacedLink key={item.slug} onClick={() => setOpenContactSlug(item.slug)}>
                <LinkAvatarContainer>{item.profile_url && <LinkAvatar alt={item.name} src={item.profile_url} />}</LinkAvatarContainer>
                <ContactDetailsContainer>
                  <TitleText variant='body1'>{item.name}</TitleText>
                  <NonOverflowText variant='caption' color='textSecondary'>
                    {contactRole}
                  </NonOverflowText>
                </ContactDetailsContainer>
              </StyledWorkspacedLink>
            ))}
          </ContactContainer>
        ) : (
          <div>No {contactRole} available.</div>
        )}
      </DetailsContainer>
    </BorderedCard>
  )
}

interface SharedContactDetailsProps {
  slug: string
}

const SharedContactDetails: React.FC<SharedContactDetailsProps> & { Skeleton: React.FC } = ({ slug }) => {
  const queryData = useLazyLoadQueryWithSubscription<SharedContactDetailsQuery>(
    query,
    { slug },
    { fetchPolicy: 'network-only', subscriptionOptions: { entityType: 'contacts', entitySlug: slug } }
  )
  const { contactShow: contact } = queryData

  return (
    <ContactRow>
      <div key='transporter-shared-details'>
        {contact.roles.includes('driver') && (
          <ContactList header='Transporters' contactRole='transporter' list={contact.sharedTransporters} slug={slug} />
        )}
      </div>
      <div key='driver-shared-details'>
        {contact.roles.includes('transporter') && (
          <ContactList header='Drivers' contactRole='driver' list={contact.sharedDrivers} slug={slug} />
        )}
      </div>
    </ContactRow>
  )
}

const query = graphql`
  query SharedContactDetailsQuery($slug: String!) {
    contactShow(slug: $slug) {
      slug
      roles
      sharedDrivers
      sharedTransporters
    }
  }
`
SharedContactDetails.Skeleton = () => <>Loading...</>
export default SharedContactDetails
